import React, { useState } from 'react';
import { sendCannedMessage, getRecentBattles } from '../../api/messagingApi';
import './SendCannedModal.css';

/**
 * SendCannedModal - Modal for sending canned messages to friends
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {number} props.friendId - Friend's user ID
 * @param {string} props.friendUsername - Friend's username
 */
function SendCannedModal({ isOpen, onClose, friendId, friendUsername }) {
    const [view, setView] = useState('options'); // 'options', 'battles', 'success'
    const [recentBattles, setRecentBattles] = useState([]);
    const [isLoadingBattles, setIsLoadingBattles] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);
    const [sentMessageType, setSentMessageType] = useState(null);

    // VALIDATE PROPS - NO SILENT FAILURES
    if (typeof isOpen !== 'boolean') {
        throw new Error('SendCannedModal: isOpen prop must be a boolean');
    }
    if (typeof onClose !== 'function') {
        throw new Error('SendCannedModal: onClose prop must be a function');
    }
    if (isOpen && !friendId) {
        throw new Error('SendCannedModal: friendId is required when modal is open');
    }
    if (isOpen && !friendUsername) {
        throw new Error('SendCannedModal: friendUsername is required when modal is open');
    }

    const handleSendGif = async () => {
        setIsSending(true);
        setError(null);

        try {
            await sendCannedMessage(friendId, 'GIF');
            console.log(`SendCannedModal: GIF sent to ${friendUsername}`);
            setSentMessageType('GIF');
            setView('success');

            // Auto-close after 2 seconds
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (err) {
            console.error('SendCannedModal: Error sending GIF:', err);
            setError(err.message || 'Failed to send GIF. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const handleSendWitty = async () => {
        setIsSending(true);
        setError(null);

        try {
            await sendCannedMessage(friendId, 'WITTY');
            console.log(`SendCannedModal: Witty comment sent to ${friendUsername}`);
            setSentMessageType('WITTY');
            setView('success');

            // Auto-close after 2 seconds
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (err) {
            console.error('SendCannedModal: Error sending witty comment:', err);
            setError(err.message || 'Failed to send message. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const handleOpenBattleSelector = async () => {
        setView('battles');
        setIsLoadingBattles(true);
        setError(null);

        try {
            const data = await getRecentBattles(10);
            setRecentBattles(data.battles || []);
            
            if (data.battles.length === 0) {
                setError('You have no recent battles to share. Complete a league first!');
            }
        } catch (err) {
            console.error('SendCannedModal: Error fetching recent battles:', err);
            setError(err.message || 'Failed to load recent battles. Please try again.');
        } finally {
            setIsLoadingBattles(false);
        }
    };

    const handleSendBattleResult = async (battle) => {
        setIsSending(true);
        setError(null);

        try {
            const battleData = {
                league_id: battle.league_id,
                league_name: battle.league_name,
                placement: battle.placement,
                score: battle.score,
                team_name: battle.team_name
            };

            await sendCannedMessage(friendId, 'BATTLE_RESULT', battleData);
            console.log(`SendCannedModal: Battle result sent to ${friendUsername}`);
            setSentMessageType('BATTLE_RESULT');
            setView('success');

            // Auto-close after 2 seconds
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (err) {
            console.error('SendCannedModal: Error sending battle result:', err);
            setError(err.message || 'Failed to send battle result. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const handleBackToOptions = () => {
        setView('options');
        setError(null);
    };

    const handleClose = () => {
        setView('options');
        setRecentBattles([]);
        setError(null);
        setSentMessageType(null);
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !isSending) {
            handleClose();
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="send-canned-modal-overlay" onClick={handleOverlayClick}>
            <div className="send-canned-modal">
                {/* Header */}
                <div className="send-canned-modal-header">
                    <h2 className="send-canned-modal-title">
                        {view === 'battles' ? 'Select Battle' : `Send to ${friendUsername}`}
                    </h2>
                    <button 
                        className="send-canned-modal-close" 
                        onClick={handleClose}
                        disabled={isSending}
                        aria-label="Close"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="send-canned-modal-body">
                    {error && (
                        <div className="send-canned-error">
                            {error}
                        </div>
                    )}

                    {view === 'options' && (
                        <div className="canned-message-options">
                            <p className="options-description">
                                Choose a message to send:
                            </p>

                            <button
                                className="canned-option-button"
                                onClick={handleSendGif}
                                disabled={isSending}
                            >
                                <span className="option-icon">🎬</span>
                                <div className="option-content">
                                    <div className="option-title">Send GIF</div>
                                    <div className="option-subtitle">Pretend this is a gif</div>
                                </div>
                            </button>

                            <button
                                className="canned-option-button"
                                onClick={handleSendWitty}
                                disabled={isSending}
                            >
                                <span className="option-icon">💬</span>
                                <div className="option-content">
                                    <div className="option-title">Send Witty Comment</div>
                                    <div className="option-subtitle">Insert witty comment here</div>
                                </div>
                            </button>

                            <button
                                className="canned-option-button"
                                onClick={handleOpenBattleSelector}
                                disabled={isSending}
                            >
                                <span className="option-icon">🏆</span>
                                <div className="option-content">
                                    <div className="option-title">Share Battle Result</div>
                                    <div className="option-subtitle">Show off your victory</div>
                                </div>
                            </button>
                        </div>
                    )}

                    {view === 'battles' && (
                        <div className="battle-selector">
                            {isLoadingBattles ? (
                                <div className="battles-loading">
                                    <div className="loading-spinner"></div>
                                    <p>Loading recent battles...</p>
                                </div>
                            ) : recentBattles.length > 0 ? (
                                <>
                                    <p className="battles-description">
                                        Select a battle to share:
                                    </p>
                                    <div className="battles-list">
                                        {recentBattles.map((battle) => (
                                            <button
                                                key={battle.league_id}
                                                className="battle-option-button"
                                                onClick={() => handleSendBattleResult(battle)}
                                                disabled={isSending}
                                            >
                                                <div className="battle-info">
                                                    <div className="battle-name">{battle.league_name}</div>
                                                    <div className="battle-details">
                                                        {battle.sport} • Week {battle.week} • {battle.season_year}
                                                    </div>
                                                </div>
                                                <div className="battle-result">
                                                    <div className="battle-placement">
                                                        {battle.placement === 1 ? '🏆' : `#${battle.placement}`}
                                                    </div>
                                                    <div className="battle-score">{battle.score.toFixed(1)} pts</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        className="back-to-options-button"
                                        onClick={handleBackToOptions}
                                        disabled={isSending}
                                    >
                                        ← Back to Options
                                    </button>
                                </>
                            ) : null}
                        </div>
                    )}

                    {view === 'success' && (
                        <div className="send-canned-success">
                            <div className="success-icon">✓</div>
                            <h3 className="success-title">Message Sent!</h3>
                            <p className="success-message">
                                Your {sentMessageType === 'GIF' ? 'GIF' : sentMessageType === 'WITTY' ? 'message' : 'battle result'} was sent to {friendUsername}.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SendCannedModal;
